/**
 * Media elements: chart, map and image. The descriptor carries registry keys
 * and domain data only; this module is the one boundary that turns them into
 * the chart element, registered map slots and governed images.
 */
import React from 'react';
import '../browseParcelPublicPictureMapSlot';
import { FmAssetImage } from '../FmAssetImage';
import { FmChart } from '../charts/FmChart';
import { FmChartBlock } from '../charts/FmChartBlock';
import { FmCorridorTrajectoryChart } from '../charts/FmCorridorTrajectoryChart';
import type { FmChartSpec } from '../charts/chartTypes';
import { ChartPaletteError } from '../charts/palette';
import { FmStack, FmText } from '../FmComposition';
import { FmLegend } from '../FmLegend';
import { FmMapLegend } from '../FmMapLegend';
import { FmPropertyBoundarySnapshot } from '../FmPropertyBoundarySnapshot';
import { FmSelector } from '../FmSelector';
import { resolveLegendSwatch } from '../mapLegendPalette';
import { chartElementSpec } from './chartElementSpec';
import type { FmStoryChart } from './elementFields.types';
import type { FmStoryMapMediaSource } from './elementMedia.types';
import type { FmChartElement, FmImageElement, FmMapElement } from './elements.types';
import { mapLegendCategorySwatch } from './mapLegendCategories';
import { FmMapSelectionList, FmMapSelectionToolbar } from './FmMapSelection';
import { useMapSelectionModel, type FmMapSelectionModel } from './mapSelectionModel';
import { hasMediaAsset } from '@/shared/config/mediaRegistry';
import {
  resolveContextualForestMediaResult,
  resolveGovernedMediaIdentity,
  type ContextualForestMediaResolution,
} from '@/shared/config/contextualForestMedia';
import {
  getMapSlot,
  PROPERTY_BOUNDARY_SNAPSHOT_SLOT_KEY,
  registerMapSlot,
  type FmMapSlotProps,
} from '@/shared/config/mapSlotRegistry';
import './FmElements.css';

registerMapSlot(PROPERTY_BOUNDARY_SNAPSHOT_SLOT_KEY, (slotProps) => {
  if (!slotProps.propertyId) {
    return <div data-state="empty" />;
  }
  return (
    <FmPropertyBoundarySnapshot
      propertyId={slotProps.propertyId}
      caption={slotProps.caption}
      size={slotProps.snapshotSize ?? 'compact'}
      revisionKey={slotProps.sourceId}
      data-testid="story-property-boundary-snapshot"
    />
  );
});

export type FmMediaCropRole = 'split' | 'card';

/** Spec building can refuse a species value by name; the chart shows that refusal. */
function buildChartSpec(input: { chart: FmStoryChart; unit: string; locale?: string }): {
  spec: FmChartSpec | null;
  error?: string;
} {
  try {
    return { spec: chartElementSpec(input) };
  } catch (error) {
    if (error instanceof ChartPaletteError) return { spec: null, error: error.message };
    throw error;
  }
}

/**
 * The chart element: the served chart through the shared Recharts element, the
 * corridor figure, or the chart chrome around an explicit unavailable reason
 * (title, caption and takeaway stay; the plot area names why it is missing).
 */
export const FmChartElementView: React.FC<{ element: FmChartElement }> = ({ element }) => {
  if (element.corridor) {
    return <FmCorridorTrajectoryChart trajectory={element.corridor} data-testid={element.testId} />;
  }
  const testId = element.testId ?? 'story-chart-media';
  if (!element.chart) {
    return (
      <FmChartBlock
        title={element.title}
        data={[]}
        unit={element.unit}
        caption={element.caption}
        takeaway={element.takeaway}
        source={element.source}
        emptyText={element.unavailableReason}
        unavailableLabel={element.unavailableReason}
        data-testid={testId}
      />
    );
  }
  const { spec, error } = buildChartSpec({
    chart: element.chart,
    unit: element.unit,
    locale: element.locale,
  });
  return (
    <FmChart
      title={element.title}
      chart={spec}
      error={error}
      unit={element.unit}
      caption={element.caption}
      takeaway={element.takeaway}
      contextDetail={element.contextDetail}
      valueKind={element.valueKind}
      explanationTitle={element.explanationTitle}
      source={element.source}
      emptyText={element.emptyLabel}
      locale={element.locale}
      data-testid={testId}
    />
  );
};

const SourceNote: React.FC<{ caption?: string; source?: FmStoryMapMediaSource }> = ({
  caption,
  source,
}) => {
  if (!caption && !source) return null;
  return (
    <FmStack gap="xs">
      {caption ? (
        <FmText size="caption" tone="muted">
          {caption}
        </FmText>
      ) : null}
      {source ? (
        <FmText size="fine" tone="muted">
          {source.label}
          {source.timestamp ? ` · ${source.timestamp}` : ''}
          {source.licenseLabel ? ` · ${source.licenseLabel}` : ''}
        </FmText>
      ) : null}
    </FmStack>
  );
};

function contextualMediaProvenance(resolution: ContextualForestMediaResolution): string {
  if (resolution.mode === 'illustrative') {
    return 'Illustrative visual context selected from served inventory attributes.';
  }
  if (resolution.evidenceKind === 'property-image') {
    return 'Property-level visual context from the active source.';
  }
  if (resolution.evidenceKind === 'other') {
    return 'Governed visual context from the active source.';
  }
  return 'Stand-level visual evidence from the active source.';
}

function imageSource(
  element: FmImageElement,
  contextual: ContextualForestMediaResolution | null,
): FmStoryMapMediaSource | undefined {
  if (element.source) return element.source;
  if (contextual) return { label: contextualMediaProvenance(contextual) };
  return element.credit ? { label: element.credit } : undefined;
}

export const FmImageElementView: React.FC<{
  element: FmImageElement;
  cropRole?: FmMediaCropRole;
}> = ({ element, cropRole = 'split' }) => {
  const contextual = element.mediaContext
    ? resolveContextualForestMediaResult(element.mediaContext)
    : null;
  const identity =
    contextual?.identity ?? resolveGovernedMediaIdentity({ assetKey: element.assetKey });
  if (!identity) {
    return (
      <div className="fm-element-unavailable" data-state="empty">
        {element.emptyLabel ?? 'Media unavailable.'}
      </div>
    );
  }
  return (
    <FmStack gap="sm" data-testid={element.testId ?? 'story-image-media'}>
      <FmAssetImage assetKey={identity} altOverride={element.altOverride} cropRole={cropRole} />
      <SourceNote caption={element.caption} source={imageSource(element, contextual)} />
    </FmStack>
  );
};

function renderMapFigure(
  element: FmMapElement,
  cropRole: FmMediaCropRole,
  selectionProps?: Partial<FmMapSlotProps>,
): React.ReactNode {
  const { figure } = element;
  if (!figure) return null;
  if (figure.kind === 'asset') {
    if (!hasMediaAsset(figure.assetKey)) return null;
    return (
      <FmAssetImage
        assetKey={figure.assetKey}
        altOverride={figure.altOverride}
        cropRole={cropRole}
      />
    );
  }
  const mapSlot = getMapSlot(figure.slotKey);
  return mapSlot ? mapSlot({ ...figure.slotProps, ...selectionProps }) : null;
}

const MapState: React.FC<{
  state: 'loading' | 'error' | 'empty';
  label: string | undefined;
  testId: string;
}> = ({ state, label, testId }) => (
  <div
    className="fm-element-unavailable"
    data-state={state}
    aria-busy={state === 'loading' ? true : undefined}
    role={state === 'error' ? 'alert' : undefined}
    data-testid={testId}
  >
    {label ?? ''}
  </div>
);

/** Steps page the map (year windows, phases); the selector sits above the frame. */
const MapSteps: React.FC<{ element: FmMapElement; testId: string }> = ({ element, testId }) => {
  const steps = element.steps ?? [];
  if (steps.length === 0) return null;
  const label = element.stepLabel ?? '';
  return (
    <div className="fm-element-map__steps">
      <FmSelector
        variant={element.stepControl ?? 'segmented'}
        label={label}
        value={element.selectedStepId ?? steps[0]?.id ?? ''}
        items={steps.map((step) => ({
          value: step.id,
          label: step.label,
          disabled: step.disabled,
          'data-testid': step.testId,
        }))}
        onChange={(stepId) => element.onStepChange?.(stepId)}
        data-testid={`${testId}-steps`}
      />
    </div>
  );
};

const MapLegend: React.FC<{ element: FmMapElement; testId: string }> = ({ element, testId }) => {
  const { legend } = element;
  if (!legend) return null;
  if (legend.categories) {
    return (
      <FmLegend
        direction="wrap"
        entries={legend.categories.map((entry) => ({
          color: mapLegendCategorySwatch(entry),
          label: entry.label,
        }))}
        data-testid={`${testId}-legend`}
      />
    );
  }
  return (
    <FmMapLegend
      title={legend.title}
      scale={legend.scale}
      units={legend.units}
      paletteName={legend.paletteName}
      noDataLabel={legend.noDataLabel}
      thresholds={legend.thresholds}
      bins={legend.bins?.map((bin) => ({
        min: bin.min,
        max: bin.max,
        label: bin.label,
        color: resolveLegendSwatch(bin.level),
      }))}
      data-testid={`${testId}-legend`}
    />
  );
};

export const FmMapElementView: React.FC<{
  element: FmMapElement;
  cropRole?: FmMediaCropRole;
  /** Map slots that were placed as `active-content` keep their historical test id. */
  defaultTestId?: string;
}> = ({ element, cropRole = 'split', defaultTestId = 'story-map-media' }) => {
  const testId = element.testId ?? defaultTestId;
  if (element.loading)
    return <MapState state="loading" label={element.emptyLabel} testId={testId} />;
  if (element.errorLabel)
    return <MapState state="error" label={element.errorLabel} testId={testId} />;
  if (element.selection) {
    return (
      <SelectableMap
        element={element}
        selection={element.selection}
        cropRole={cropRole}
        testId={testId}
      />
    );
  }
  const figure = renderMapFigure(element, cropRole);
  if (!figure) return <MapState state="empty" label={element.emptyLabel} testId={testId} />;
  return <MapFrame element={element} figure={figure} testId={testId} />;
};

const MapFrame: React.FC<{
  element: FmMapElement;
  figure: React.ReactNode;
  testId: string;
  selection?: { model: FmMapSelectionModel; value: NonNullable<FmMapElement['selection']> };
}> = ({ element, figure, testId, selection }) => (
  <FmStack
    gap="sm"
    className="fm-element-map"
    data-testid={testId}
    data-selection-mode={selection ? 'multiple' : undefined}
  >
    <MapSteps element={element} testId={testId} />
    {selection ? (
      <FmMapSelectionToolbar selection={selection.value} model={selection.model} testId={testId} />
    ) : null}
    <div className="fm-element-map__figure">{figure}</div>
    <MapLegend element={element} testId={testId} />
    {selection ? (
      <FmMapSelectionList selection={selection.value} model={selection.model} testId={testId} />
    ) : null}
    <SourceNote caption={element.interactionHint} source={element.source} />
  </FmStack>
);

/** Multi-select mode: the slot draws the chosen set; toolbar and list choose too. */
const SelectableMap: React.FC<{
  element: FmMapElement;
  selection: NonNullable<FmMapElement['selection']>;
  cropRole: FmMediaCropRole;
  testId: string;
}> = ({ element, selection, cropRole, testId }) => {
  const model = useMapSelectionModel(selection);
  const figure = renderMapFigure(element, cropRole, model.slotProps);
  if (!figure) return <MapState state="empty" label={element.emptyLabel} testId={testId} />;
  return (
    <MapFrame
      element={element}
      figure={figure}
      testId={testId}
      selection={{ model, value: selection }}
    />
  );
};
